import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SUPPORTED = ["en", "ja"]; // 'it' is default at root (no prefix)

/**
 * Detects the language prefix from the URL path.
 * Returns:
 *   - lang: current language code ("it" | "en" | "ja")
 *   - prefix: URL prefix ("" for it, "/en" for en, "/ja" for ja)
 *   - buildPath(to): prepends prefix to an internal path
 *   - buildPathFor(lang, to): builds a path in a specific language
 */
const useLangPath = () => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  // Detect from URL first (source of truth)
  const segments = pathname.split("/").filter(Boolean);
  let lang = "it";
  if (segments.length > 0 && SUPPORTED.includes(segments[0])) {
    lang = segments[0];
  }

  const prefix = lang === "it" ? "" : `/${lang}`;

  const buildPath = (to) => buildPathFor(lang, to);

  const i18nLang = (i18n.resolvedLanguage || i18n.language || "it").slice(0, 2);

  return { lang, prefix, buildPath, buildPathFor, i18nLang };
};

/** Builds a fully prefixed path (external helper, no hook). */
export const buildPathFor = (lang, to) => {
  if (!to) return lang === "it" ? "/" : `/${lang}`;
  // If path starts with a hash, keep as-is (in-page anchor)
  if (to.startsWith("#")) return to;
  const langPrefix = lang === "it" ? "" : `/${lang}`;
  // Detect the base and preserve trailing hash (#contatti)
  const [rawPath, hash] = to.split("#");
  // Strip leading slash to normalise
  const clean = rawPath.replace(/^\/+/, "");
  // Handle root or empty
  if (!clean) {
    return `${langPrefix || "/"}${hash ? `#${hash}` : ""}`;
  }
  return `${langPrefix}/${clean}${hash ? `#${hash}` : ""}`;
};

export default useLangPath;
